import Big from 'big.js'
import { useTourGuide } from 'context/tour-guide/use-tour-guide'
import { AnalyticsArea, useAnalyticsEventTracker } from 'hooks/analytics-tracker'
import React, { Fragment } from 'react'
import { currencyFormat } from 'utils'
import {
  MenuListContainer,
  MenuSection,
  MenuTitle,
  NavigationItem,
  NestedMenuItem,
  NestedMenuItems,
  NestedMenuOutline,
  NestedMenuTextWrapper,
  NestedMenuWrapper,
  NestedProductIcon,
  NestedSvgIcon,
  OpenMenulistContainer,
  PortfolioLine,
  StyledSubtitle,
  StyledTitle,
} from '../../styling/desktop'
import type { HeaderStorage } from '../storage/interface'
import { type NestedNavListProps, useActiveLinking } from '../use-active-linking'
import { getProcessedValue } from './utils'

const getCurrentPrice = (price: Big) => {
  if (price.gt(0)) {
    return currencyFormat(price)
  }
  return '$0.00'
}

/**
 * React component that handles the rendering of navigation items for desktop mode
 */
const DesktopNavigation: React.FC<HeaderStorage> = ({ portfolioReturns }) => {
  const investedValue = portfolioReturns.investedCapital ?? Big(0)
  const { navigationList, nestedNavigationList } = useActiveLinking()
  const [nestedMenuItem, setNestedMenuItem] = React.useState<NestedNavListProps | undefined>()
  const [list, setList] = React.useState<NestedNavListProps[]>(
    nestedNavigationList(
      getCurrentPrice(investedValue),
      getProcessedValue(portfolioReturns).toString(),
    ),
  )

  const { navigationEvent } = useAnalyticsEventTracker(AnalyticsArea.NavigationBar)
  const { currentStep, startTour, menuOpen, setCurrentStep, setMenuOpen } = useTourGuide()

  const handleNavigate = (url: string, external?: boolean) => {
    if (menuOpen === 'OPEN' && currentStep === 12) {
      setMenuOpen('CLOSED')
      setCurrentStep(13)
    }
    navigationEvent(url, external)
    setNestedMenuItem(undefined)
  }

  const pickNavItem = (index: number) => list && setNestedMenuItem(list[index])
  React.useEffect(() => {
    let timer: NodeJS.Timeout
    if (menuOpen === 'OPEN') {
      pickNavItem(1)
      timer = setTimeout(() => {
        startTour()
        setCurrentStep(12)
      }, 500)
    }

    if (menuOpen === 'CLOSED') {
      setNestedMenuItem(undefined)
    }

    return () => clearTimeout(timer)
  }, [menuOpen, currentStep])

  React.useEffect(() => {
    setNestedMenuItem(undefined)
    setList(
      nestedNavigationList(
        menuOpen !== 'OPEN' ? getCurrentPrice(investedValue) : '6496.98',
        menuOpen !== 'OPEN' ? getProcessedValue(portfolioReturns).toString() : '58.56',
      ),
    )
  }, [menuOpen, portfolioReturns])

  const menuIsOpen = menuOpen === 'OPEN'
  const isActive = menuIsOpen || nestedMenuItem !== undefined
  const isCompany = (nestedMenuItem: NestedNavListProps) =>
    nestedMenuItem && nestedMenuItem.title === 'Company' && !menuIsOpen
  const isHighlightable = (nestedMenuItem: NestedNavListProps, index: number) =>
    !nestedMenuItem.isPortfolio || (nestedMenuItem.isPortfolio && index <= 1)
  const isPortfolio = (nestedMenuItem: NestedNavListProps, index: number) => {
    return menuIsOpen || (nestedMenuItem.isPortfolio && index <= 1)
  }

  const handleNestedMenuMouseLeave = () => {
    if (menuIsOpen) {
      return
    }
    return setNestedMenuItem(undefined)
  }

  return (
    <MenuSection>
      {navigationList.map((path, index) => (
        <NavigationItem
          id={`tour-navigation-${path}`}
          key={`${path}-${index}`}
          onMouseEnter={() => {
            if (!menuIsOpen) {
              pickNavItem(index)
            }
          }}
        >
          {path}
        </NavigationItem>
      ))}
      <NestedMenuOutline isActive={isActive}>
        <MenuListContainer>
          <NestedMenuWrapper onMouseLeave={() => handleNestedMenuMouseLeave()}>
            {/* <NestedMenuWrapper> */}
            {nestedMenuItem && (
              <OpenMenulistContainer>
                <MenuTitle>{nestedMenuItem.title}</MenuTitle>
                <NestedMenuItems isLarge={nestedMenuItem.title === 'Products'}>
                  {nestedMenuItem.data.map((props, index) => {
                    const Icon = props.icon
                    return (
                      <Fragment key={`${props.name}-${props.description}`}>
                        <NestedMenuItem
                          id={`${props.name.trim().replace(' ', '-').toLowerCase()}`}
                          highlightable={isHighlightable(nestedMenuItem, index)}
                          isPortfolio={isPortfolio(nestedMenuItem, index)}
                          isCompany={isCompany(nestedMenuItem)}
                          onClick={() =>
                            props.link &&
                            handleNavigate(
                              props.link,
                              props.name === 'Documentation' || props.name === 'Blog',
                            )
                          }
                        >
                          {nestedMenuItem.title === 'Products' && typeof props.icon === 'string' ? (
                            <NestedProductIcon src={props.icon} />
                          ) : (
                            <NestedSvgIcon
                              icon={Icon as React.FunctionComponent<React.SVGProps<SVGSVGElement>>}
                            />
                          )}
                          <NestedMenuTextWrapper>
                            {/* TODO handle is zero */}
                            <StyledTitle
                              isPositive={props.colored && Number(props.name) >= 0}
                              isZero={Number(props.name) === 0}
                            >
                              {!props.colored
                                ? props.name
                                : `${Number(props.name) > 0 ? '+' : ''}${props.name}%`}
                            </StyledTitle>
                            <StyledSubtitle>{props.description}</StyledSubtitle>
                          </NestedMenuTextWrapper>
                        </NestedMenuItem>
                        {nestedMenuItem.isPortfolio && index === 1 && <PortfolioLine />}
                      </Fragment>
                    )
                  })}
                </NestedMenuItems>
              </OpenMenulistContainer>
            )}
          </NestedMenuWrapper>
        </MenuListContainer>
      </NestedMenuOutline>
    </MenuSection>
  )
}
export default DesktopNavigation
